import { useState } from "react";
import { AlertTriangle, Lock } from "lucide-react";
import { ApprovalActions, ApproveErrorBanner } from "./ApprovalActions";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Input } from "@/components/atoms/studio/ui/input";
import { STATUS_DOT_MAP } from "@/constants/multi-agent/status.constants";
import { useEngineApproval } from "@/hooks/migrations/engine";
import { approvalGateState, type ApprovalGateState } from "@/lib/migrations/apply";
import { suppressedTone, toneBadgeVariant } from "@/lib/migrations/coverage";
import {
  ENGINE_AWAITING_APPROVAL,
  engineErrorMessage,
  isApprovalConflict,
} from "@/services/migrations/engine";
import type { EnginePlanReport, EngineTaskReport } from "@/services/migrations/engine";
import type { TaskReviewState } from "./reviewState";

/**
 * The engine's approval gate.
 *
 * WHAT APPROVAL MEANS NOW. It resumes a paused plan AND it writes a ledger row naming the
 * exact `compile_key` of every task it moves. That row is one of the five factors the
 * write path requires before it will create anything in a customer's org. So this is no
 * longer "resume the run" with a disclaimer — it is the human half of an authorisation,
 * and it still does not deploy: the artifact goes to the org through a separate, verbatim
 * act, later, possibly by someone else.
 *
 * WHY IT REFUSES TO OFFER ITSELF. `compile_key` is a hash over every input that can
 * change a byte. An approval recorded against an artifact the approver did not read is a
 * valid approval by every check the server makes — the ledger row will say "approved by
 * <name>" and the write path will honour it. The only place that can be prevented is
 * here, before the row exists. So:
 *
 *   * every task at the gate must have its artifact list loaded,
 *   * the CURRENT version of each one must have its body open on screen,
 *   * and the compile key on screen must equal the task's.
 *
 * Fail any of those and Approve/Reject are not rendered disabled-with-a-tooltip; the
 * blockers are printed as the reason, and the buttons do not appear until they clear.
 * Approving here is bulk — one decision covers every task at the gate — which is exactly
 * why every one of them has to have been read.
 *
 * The store wiring is `useEngineApproval` (react-query), which mirrors
 * `testingStore.approvePlanForRun`: one in-flight decision at a time, the error kept and
 * shown rather than swallowed, and the plan + plans-list queries invalidated on success
 * because both are stale the moment approval returns.
 */
export function EngineApprovalGate({
  report,
  successSuppressed,
  review,
}: {
  report: EnginePlanReport;
  successSuppressed: boolean;
  /** What each task card is currently showing, keyed by task key. */
  review: Record<string, TaskReviewState>;
}) {
  const [reason, setReason] = useState("");
  const [conflict, setConflict] = useState<string | null>(null);
  const approval = useEngineApproval(report.plan_id);

  const awaiting = report.status === ENGINE_AWAITING_APPROVAL;
  const pendingTasks = report.tasks.filter((task) => task.status === ENGINE_AWAITING_APPROVAL);
  const taskKeys = pendingTasks.map((task) => task.key);

  // The gate only exists while something is actually waiting on it. Rendering a
  // permanently-disabled Approve button would invite the click it cannot honour.
  if (!awaiting) return null;

  const gates = pendingTasks.map((task) => ({
    task,
    state: gateFor(task, review[task.key], successSuppressed),
  }));
  const canDecide = gates.every((entry) => entry.state.canDecide);

  const decide = (decision: "approved" | "rejected") => {
    setConflict(null);
    approval.mutate(
      {
        task_keys: taskKeys,
        decision,
        gate: "GATE_APPLY",
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      },
      {
        onError: (error) => {
          // 409 is the gate itself talking: nothing is awaiting a decision any more,
          // usually because another tab or another person already decided. That is a
          // "you are looking at a stale screen" state, not a failure to report as red.
          if (isApprovalConflict(error)) {
            setConflict(
              engineErrorMessage(error) ||
                "Nothing on this plan is awaiting a decision — it may have been decided already."
            );
          }
        },
        onSuccess: () => setReason(""),
      }
    );
  };

  const nonConflictError =
    approval.error && !isApprovalConflict(approval.error)
      ? engineErrorMessage(approval.error)
      : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Reuses the existing multi-agent label so one status has one name app-wide. */}
        <Badge variant={toneBadgeVariant(suppressedTone("success", successSuppressed))}>
          {STATUS_DOT_MAP[ENGINE_AWAITING_APPROVAL].label}
        </Badge>
        <span className="font-mono text-[11px] text-muted-foreground">
          {taskKeys.length} task{taskKeys.length === 1 ? "" : "s"} paused
        </span>
      </div>

      <p className="max-w-prose text-xs text-muted-foreground">
        Approving <span className="font-semibold text-foreground">resumes this plan</span> and
        records a decision naming each task&apos;s exact compile key. It does not deploy: writing
        to your org is a separate act with its own confirmation, and this decision becomes void
        the moment any of these artifacts recompiles.
      </p>

      {/* ── what the approver is being asked to vouch for ──────────────────── */}
      {gates.map(({ task, state }) => (
        <TaskGateSummary key={task.key} task={task} state={state} />
      ))}

      {conflict ? (
        <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--accent-warm))]/40 bg-accent-warm-soft p-2 text-xs text-accent-warm">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 leading-relaxed">{conflict}</span>
        </div>
      ) : null}

      {nonConflictError ? (
        <ApproveErrorBanner
          message={nonConflictError}
          approving={approval.isPending}
          onRetry={() => decide("approved")}
          onDismiss={() => approval.reset()}
        />
      ) : null}

      {canDecide ? (
        <>
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Reason (optional) — recorded with the decision"
            disabled={approval.isPending}
            className="max-w-lg"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            <ApprovalActions
              approving={approval.isPending}
              onApprove={() => decide("approved")}
              onReject={() => decide("rejected")}
              disabled={Boolean(conflict)}
            />
          </div>
        </>
      ) : (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 leading-relaxed">
            No decision can be recorded from this screen yet. An approval names a specific
            compiled artifact, and the reasons above say which ones are not on screen.
          </span>
        </div>
      )}
    </div>
  );
}

function gateFor(
  task: EngineTaskReport,
  state: TaskReviewState | undefined,
  silentLoss: boolean
): ApprovalGateState {
  return approvalGateState({
    task,
    // Absent review state means the task card has not reported in — which is the same
    // situation as "not loaded" and is treated as such rather than assumed benign.
    artifactsLoaded: state?.loaded ?? false,
    artifacts: state?.artifacts ?? [],
    displayed: state?.displayed ?? null,
    silentLoss,
  });
}

/** One task's readiness, and everything the approver has to be told before deciding. */
function TaskGateSummary({ task, state }: { task: EngineTaskReport; state: ApprovalGateState }) {
  return (
    <div className="space-y-1.5 rounded-md border border-border bg-muted/20 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate font-mono text-xs text-foreground">{task.key}</span>
        <Badge variant={state.canDecide ? "secondary" : "warm"}>
          {state.canDecide ? "reviewed" : "not reviewed"}
        </Badge>
        {task.apply.will_create.map((item) => (
          <span
            key={`${item.metadata_type}-${item.component_key}`}
            className="break-all font-mono text-[11px] text-muted-foreground"
          >
            will create {item.metadata_type} {item.component_key}
          </span>
        ))}
      </div>

      {state.blockers.length > 0 ? (
        <ul className="space-y-1">
          {state.blockers.map((blocker) => (
            <li key={blocker.code} className="flex items-start gap-2 text-xs text-destructive">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1 leading-relaxed">{blocker.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {state.warnings.length > 0 ? (
        <ul className="space-y-1">
          {state.warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${index}`}
              className={[
                "flex items-start gap-2 text-xs",
                warning.code === "SILENT_LOSS" || warning.code === "VALIDATION_FAILED"
                  ? "text-destructive"
                  : "text-[hsl(var(--accent-warm))]",
              ].join(" ")}
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1 leading-relaxed">{warning.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="break-all font-mono text-[10px] text-muted-foreground/70">
        compile key {task.compile_key || "—"}
      </p>
    </div>
  );
}
